// Account-wide key-outage status (epic #5099, slice #5119). Reads
// `GET /v1/key-outage` — the single-account lookup of the currently open key
// outage, the data behind the dashboard's account-wide banner — and asks for
// a label pick with `POST /v1/key-outage/pick` once the operator chooses one.
//
// `pick_labels` is the labels a pick may name: the non-null labels of
// `keyOutagePickableKeys(snapshot)`, the keys that serve EVERY held step. It is
// `[]` for a draining outage or when no labelled key serves every held step.
// The picker offers exactly that list, in the order the server sent it — the
// server is the arbiter of which labels may be named (`pick` may reject a label
// it never advertised).
//
// The wire is PARSED, not cast: a payload with `outage` set but
// `pick_labels` missing would render a banner with no control, the same defect
// `lib/email-status.ts` parses its own read against. Only the fields the banner
// reads are listed; unread fields are ignored.
//
// The pick response is parsed too, against the server's actual 200 body
// (`{outage_id, key_label, tasks, asks}` per `apps/api/src/routes/key-outage.ts`
// `renderPickResult` `case 'picked'`). The banner re-reads on settle and never
// reads the echo, so a loose schema here is enough — but parsing prevents the
// pin code from drifting and the unit-test stub from locking in a fabricated
// shape.

import { useLocation } from '@solidjs/router';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { orgFromPath } from './org';
import { SESSION_TOKEN } from './session';

const KeyOutageStepSchema = z.object({
  step: z.string(),
  tasks: z.number().int().nonnegative(),
  asks: z.number().int().nonnegative(),
});

/** One row of the outage's `held` array; the banner reads only its `hold_reason`. */
const KeyOutageHeldSchema = z.object({
  hold_reason: z.string(),
});

const KeyOutageReadSchema = z.object({
  outage: z.union([
    z.object({
      id: z.string(),
      opened_at: z.string(),
      updated_at: z.string(),
      held: z.array(KeyOutageHeldSchema).readonly(),
      held_by_step: z.array(KeyOutageStepSchema).readonly(),
      pick_labels: z.array(z.string()).readonly(),
    }),
    z.null(),
  ]),
});

const KeyOutagePickSchema = z.object({
  outage_id: z.string(),
  key_label: z.string(),
  tasks: z.number().int().nonnegative(),
  asks: z.number().int().nonnegative(),
});

export type KeyOutageRead = z.infer<typeof KeyOutageReadSchema>;
export type KeyOutagePick = z.infer<typeof KeyOutagePickSchema>;
export type KeyOutageOutage = NonNullable<KeyOutageRead['outage']>;
export type KeyOutageStep = KeyOutageOutage['held_by_step'][number];

export const KEY_OUTAGE_QUERY_KEY = 'key_outage';

/**
 * The account's open key outage, or null when none is open. Re-reads on the same
 * 30s rhythm `usePlan`/`useCustomPlans` poll at (a held task moves without a
 * reload). Idle when the session is gone or the URL carries no org — admin
 * routes and onboarding have no `/org/<login>/` segment, and a banner that
 * fired on those would be a notice about nothing.
 *
 * `useLocation` is the reactive source for the gate: the shell mounts once
 * and a client-side navigation from a no-org path into `/org/<x>/…` would
 * otherwise leave the read disabled.
 */
export function useKeyOutage(token: string | null = SESSION_TOKEN) {
  const location = useLocation();
  return createQuery<KeyOutageRead>(() => ({
    queryKey: [KEY_OUTAGE_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null && orgFromPath(location.pathname) !== null,
    refetchInterval: 30_000,
    queryFn: () => apiFetch('/v1/key-outage', token, undefined, { schema: KeyOutageReadSchema }),
  }));
}

/**
 * Name a labelled key for the open outage. The server validates that
 * `keyLabel` is in the outage's `pick_labels`; a 404 / 409 / 422 from the API
 * lands in the mutation's `error()` for the banner to render verbatim.
 */
export async function pickKeyOutageKey(
  keyLabel: string,
  token: string | null = SESSION_TOKEN,
): Promise<KeyOutagePick> {
  return apiFetch(
    '/v1/key-outage/pick',
    token,
    { method: 'POST', body: JSON.stringify({ key_label: keyLabel }) },
    { schema: KeyOutagePickSchema },
  );
}
